import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Card, Form, Button, Typography, Modal, Spin, message, DatePicker, List } from "antd";
import { CloudDownloadOutlined, SyncOutlined, DownloadOutlined } from "@ant-design/icons";
import { Device } from "./types";
import { useAuth } from "../../../context/AuthContext";
import { getAuthIdToken } from "../../../firebase/firebase";
import axios from "axios";
import dayjs from "dayjs";

const { Text } = Typography;

function deviceStorageKey(device: Device): string {
  return (device.id || device.serialNumber || "").trim();
}

async function readBlobErrorMessage(data: unknown, status: number, fallback: string): Promise<string> {
  try {
    if (data instanceof Blob) {
      const text = await data.text();
      if (text.trim().startsWith("{")) {
        const json = JSON.parse(text) as { error?: string };
        if (json.error) return json.error;
      }
      if (text.trim().length > 0 && text.trim().length < 200) {
        return text.trim();
      }
    } else if (data && typeof data === "object" && "error" in data) {
      const err = String((data as { error?: string }).error || "").trim();
      if (err) return err;
    }
  } catch {
    // ignore
  }
  return `${fallback}（HTTP ${status}）`;
}

export type DeviceLogEditorHandle = {
  openRequest: () => void;
  openDownload: () => void;
};

interface Props {
  selectedDevice: Device | null;
}

const DeviceLogEditor = forwardRef<DeviceLogEditorHandle, Props>(({ selectedDevice }, ref) => {
  const { currentUser } = useAuth();
  const [isLogRequestModalVisible, setIsLogRequestModalVisible] = useState(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [logFiles, setLogFiles] = useState<{ name: string; fullPath: string }[]>([]);
  const [isFetchingLogs, setIsFetchingLogs] = useState(false);

  const fetchLogFiles = useCallback(async () => {
    if (!selectedDevice) return;
    const key = deviceStorageKey(selectedDevice);
    if (!key) {
      message.error("デバイスIDが不正です。");
      return;
    }
    setIsFetchingLogs(true);
    setLogFiles([]);

    try {
      // Authorization: Bearer は Hosting→Functions で GFE が先に 401 にすることがあるため、body の idToken のみ使う
      const idToken = await getAuthIdToken();
      const res = await axios.post<{
        success?: boolean;
        files?: { name: string; fullPath: string }[];
        error?: string;
      }>(
        "/api/listAndroidLogs",
        { deviceId: key, idToken },
        { headers: { "Content-Type": "application/json" } }
      );
      const files = Array.isArray(res.data?.files) ? res.data.files : [];
      files.sort((a, b) => b.name.localeCompare(a.name));
      setLogFiles(files);
    } catch (error) {
      console.error("ログファイルの取得エラー:", error);
      const err = error as { response?: { data?: { error?: string }; status?: number } };
      const status = err.response?.status;
      const apiError = err.response?.data?.error;
      if (status === 401) {
        message.error(apiError || "認証に失敗しました。再ログインしてからやり直してください。");
      } else if (status === 403) {
        message.error(apiError || "管理者のみログ一覧を取得できます。");
      } else {
        message.error(
          apiError ||
            "ログファイル一覧の取得に失敗しました。端末へリクエスト後、少し待ってから更新してください。"
        );
      }
    } finally {
      setIsFetchingLogs(false);
    }
  }, [selectedDevice]);

  useEffect(() => {
    if (isDownloadModalVisible) {
      void fetchLogFiles();
    }
  }, [isDownloadModalVisible, fetchLogFiles]);

  const handleOpenLogRequestModal = useCallback(() => {
    if (!selectedDevice) {
      message.warning("デバイスを選択してください。");
      return;
    }
    setSelectedDates([]);
    setIsLogRequestModalVisible(true);
  }, [selectedDevice]);

  const handleCloseLogRequestModal = () => {
    if (isSending) return;
    setIsLogRequestModalVisible(false);
    setSelectedDates([]);
  };

  const handleDatesChange = (dates: dayjs.Dayjs[] | null) => {
    setSelectedDates((dates ?? []).map((d) => d.format("YYYY-MM-DD")));
  };

  const handleOpenDownloadModal = useCallback(() => {
    if (!selectedDevice) {
      message.warning("デバイスを選択してください。");
      return;
    }
    setIsDownloadModalVisible(true);
  }, [selectedDevice]);

  useImperativeHandle(
    ref,
    () => ({
      openRequest: handleOpenLogRequestModal,
      openDownload: handleOpenDownloadModal,
    }),
    [handleOpenLogRequestModal, handleOpenDownloadModal]
  );

  const handleSendLogRequest = async () => {
    if (!selectedDevice || selectedDates.length === 0) {
      message.error("デバイスと取得日を少なくとも1つ選択してください。");
      return;
    }
    if (!selectedDevice.fcmToken) {
      message.error("このデバイスにはFCMトークンが登録されていません。");
      return;
    }

    const key = deviceStorageKey(selectedDevice);
    setIsSending(true);
    try {
      const payload = {
        mode: "request",
        command: "upload_timber_log",
        user: currentUser?.displayName || "system",
        place: "WEB",
        param1: JSON.stringify(selectedDates),
        param2: key,
        param3: String(Date.now()),
      };

      await axios.post("/api/sendFCM", {
        tokens: [selectedDevice.fcmToken],
        payload,
      });

      message.success("ログ取得リクエストを送信しました。端末のアップロード完了後、「ログのダウンロード」から取得できます。");
      setIsLogRequestModalVisible(false);
      setSelectedDates([]);
    } catch (error) {
      console.error("FCM通知の送信に失敗しました:", error);
      message.error("ログ取得リクエストの送信に失敗しました。");
    } finally {
      setIsSending(false);
    }
  };

  const handleDownloadFile = async (filePath: string, fileName: string) => {
    const downloadMessageKey = "download-message";
    try {
      message.loading({ content: `${fileName}をダウンロード中です...`, key: downloadMessageKey, duration: 0 });

      if (!currentUser) {
        throw new Error("ログインが必要です。");
      }

      const idToken = await getAuthIdToken();
      // Authorization ヘッダは付けない（GFE が Firebase ID トークンを拒否して 401 になるため）
      // 署名 URL（signBlob）は使わずストリーム取得。idToken は body で送る。
      const fileRes = await axios.post(
        "/api/downloadAndroidLog",
        { filePath, idToken },
        {
          headers: { "Content-Type": "application/json" },
          responseType: "blob",
          validateStatus: () => true,
        }
      );
      if (fileRes.status < 200 || fileRes.status >= 300) {
        const apiError = await readBlobErrorMessage(
          fileRes.data,
          fileRes.status,
          "ファイルのダウンロードに失敗しました"
        );
        throw new Error(apiError);
      }

      const blob = fileRes.data as Blob;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success({ content: `${fileName}をダウンロードしました。`, key: downloadMessageKey });
    } catch (error: unknown) {
      console.error("ダウンロードエラー:", error);
      const err = error as { code?: string; message?: string; response?: { data?: { error?: string } } };
      const errorMessage =
        err.response?.data?.error || err.message || "ファイルのダウンロードに失敗しました。";
      message.error({ content: errorMessage, key: downloadMessageKey });
    }
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    const today = dayjs().endOf("day");
    const oneWeekAgo = dayjs().subtract(7, "days").startOf("day");
    return current.isBefore(oneWeekAgo) || current.isAfter(today);
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CloudDownloadOutlined />
            <span>ログ取得リクエスト - {selectedDevice?.deviceName || selectedDevice?.serialNumber}</span>
          </div>
        }
        open={isLogRequestModalVisible}
        onCancel={handleCloseLogRequestModal}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={handleCloseLogRequestModal} disabled={isSending}>
            キャンセル
          </Button>,
          <Button key="submit" type="primary" loading={isSending} onClick={() => void handleSendLogRequest()}>
            リクエスト送信
          </Button>,
        ]}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          端末の日次ログ（例外・WARN以上の logcat）を取得します。アプリが新バージョンで起動済みである必要があります。
        </Text>
        <Form layout="vertical">
          <Form.Item
            label="取得する日付を選択してください（複数選択可）"
            help="本日より過去7日間の範囲で選択できます。端末にログが無い日はスキップされます。"
          >
            <DatePicker
              multiple
              value={selectedDates.map((d) => dayjs(d))}
              onChange={handleDatesChange}
              disabledDate={disabledDate}
              format="YYYY-MM-DD"
              placeholder="日付を選択"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="ログのダウンロード"
        open={isDownloadModalVisible}
        onCancel={() => setIsDownloadModalVisible(false)}
        width={600}
        footer={[
          <Button key="refresh" icon={<SyncOutlined />} onClick={() => void fetchLogFiles()} disabled={isFetchingLogs}>
            更新
          </Button>,
          <Button key="close" onClick={() => setIsDownloadModalVisible(false)}>
            閉じる
          </Button>,
        ]}
      >
        {selectedDevice && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Text strong>デバイスID: </Text>
            <Text>{deviceStorageKey(selectedDevice)}</Text>
            <br />
            <Text strong>デバイス名: </Text>
            <Text>{selectedDevice.deviceName || "N/A"}</Text>
            <br />
            <Text strong>ユーザー名: </Text>
            <Text>{selectedDevice.userInfo?.userName || selectedDevice.userDisplayName || "N/A"}</Text>
          </Card>
        )}
        <Spin spinning={isFetchingLogs}>
          <div style={{ height: "40vh", overflowY: "auto", border: "1px solid #f0f0f0", padding: "8px" }}>
            <List
              dataSource={logFiles}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <Button
                      key="dl"
                      icon={<DownloadOutlined />}
                      onClick={() => void handleDownloadFile(file.fullPath, file.name)}
                    >
                      ダウンロード
                    </Button>,
                  ]}
                >
                  {file.name}
                </List.Item>
              )}
              locale={{ emptyText: "ログファイルが見つかりません。先に「ログ取得リクエスト」を送信してください。" }}
            />
          </div>
        </Spin>
      </Modal>
    </>
  );
});

DeviceLogEditor.displayName = "DeviceLogEditor";

export default DeviceLogEditor;
